import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonicModule, LoadingController, ModalController, PopoverController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { EditKnowledgeBaseItemComponent } from 'src/app/components/edit-knowledge-base-item/edit-knowledge-base-item.component';
import { EditTagLinksComponent } from 'src/app/components/edit-tag-links/edit-tag-links.component';
import { routes } from 'src/app/constants';
import { PATHS } from 'src/app/cx-home/cx-home.component';
import { KnowledgeBaseArticle, WhereClause } from 'src/models';
import { ENTITY_TYPES } from 'src/models/entities';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { KnowledgeBaseArticlesService } from 'src/services/knowledge-base-articles.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, SharedPipesModule],
  selector: 'app-knowledge-base-articles',
  templateUrl: './knowledge-base-articles.component.html',
  styleUrls: ['./knowledge-base-articles.component.scss'],
})
export class KnowledgeBaseArticlesComponent implements OnInit {
  knowledgeBaseArticles: Partial<KnowledgeBaseArticle>[] = [];
  selectedKnowledgeBaseArticles: Partial<KnowledgeBaseArticle>[] = [];
  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  selectedJobId: string;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private knowledgeBaseArticlesService: KnowledgeBaseArticlesService,
    private msgSrvc: MessageService,
    private searchablePopoverService: SearchablePopoverService,
    private tagsService: TagsService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.getKnowledgeBaseArticles();

    this.route.queryParams.subscribe(async (params) => {
      if (this.selectedJobId !== params.job) {
        this.selectedJobId = params.job;
        await this.getKnowledgeBaseArticles();
        this.selectedKnowledgeBaseArticles = [];
      }
    });
  }

  async getKnowledgeBaseArticles() {
    this.isLoading = true;
    try {
      const where: WhereClause[] = [];

      if (this.selectedJobId) {
        where.push({
          field: 'knowledgeBaseScrapeJobId',
          operator: '==',
          value: this.selectedJobId,
        });
      }

      if (this.hasValidSearchText()) {
        where.push({
          field: 'title',
          operator: 'like',
          value: this.searchText,
        });
      }

      const knowledgeBaseArticlesResponse = await this.knowledgeBaseArticlesService.list({
        limit: null,
        order: ['title'],
        descending: false,
        where,
      });

      this.knowledgeBaseArticles = knowledgeBaseArticlesResponse.data ?? [];

      // ngx-datatable is using an array of objects to keep track of selected
      // items. the array of items in the datatable is changed with every
      // refresh and object equality becomes invalid. this is a workaround to
      // keep track of selected elements or selected elements will not render
      // as selected in the table.
      this.selectedKnowledgeBaseArticles = this.selectedKnowledgeBaseArticles.map((article) => {
        const updated = this.knowledgeBaseArticles.find((k) => k.id === article.id);
        return updated ?? article;
      });
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  handleFilters() {
    // handle the case where an existing search text is deleted
    if (this.searchText === '' || this.hasValidSearchText()) {
      this.getKnowledgeBaseArticles();
    }
  }

  hasValidSearchText() {
    return this.searchText && this.searchText.trim().length > 2;
  }

  async createKnowledgeBaseArticle() {
    const buttonElement = document.activeElement as HTMLElement; // Get the currently focused element
    buttonElement.blur(); // Remove focus from the button
    const modal = await this.modalCtrl.create({
      component: EditKnowledgeBaseItemComponent,
      backdropDismiss: false,
      componentProps: {
        type: 'scrape-job',
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getKnowledgeBaseArticles();
    });
  }

  async editKnowledgeBaseArticle(id: string) {
    this.router.navigate(['./', id, 'edit'], { relativeTo: this.route });
  }

  onSelect({ selected }) {
    this.selectedKnowledgeBaseArticles.splice(0, this.selectedKnowledgeBaseArticles.length);
    this.selectedKnowledgeBaseArticles.push(...selected);
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: `Delete ${this.selectedKnowledgeBaseArticles.length} articles?`,
      subHeader: `This can't be undone.`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Confirm',
          handler: async () => {
            alert.dismiss();

            const loader = await this.loadingCtrl.create({
              message: 'Deleting...',
              spinner: 'crescent',
            });

            loader.present();

            try {
              await this.knowledgeBaseArticlesService.bulkDelete(this.selectedKnowledgeBaseArticles.map((a) => a.id));
              this.selectedKnowledgeBaseArticles = [];
              await this.getKnowledgeBaseArticles();
              loader.dismiss();
            } catch (err: any) {
              loader.dismiss();
              this.msgSrvc.showError(err);
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async editTags() {
    const modal = await this.modalCtrl.create({
      component: EditTagLinksComponent,
      componentProps: {
        objectIds: this.selectedKnowledgeBaseArticles.map((c) => c.id),
        addCallback: async (tagId, knowledgeBaseArticleIds) => {
          await this.tagsService.createKnowledgeBaseArticleLinks(tagId, knowledgeBaseArticleIds);
          await this.getKnowledgeBaseArticles();
        },
        removeCallback: async (tagId, knowledgeBaseArticleIds) => {
          await this.tagsService.deleteKnowledgeBaseArticleLinks(tagId, knowledgeBaseArticleIds);
          await this.getKnowledgeBaseArticles();
        },
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.selectedKnowledgeBaseArticles = [];
    });
  }

  async showTagOption(articleId: string, tagId: string) {
    const LABEL_REMOVE_TAG = 'Remove tag';

    const options: ActionDropdownType[] = [
      {
        label: LABEL_REMOVE_TAG,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === LABEL_REMOVE_TAG) {
            await this.tagsService.deleteKnowledgeBaseArticleLinks(tagId, [articleId]);
            await this.getKnowledgeBaseArticles();
          }
        },
      },
    });
    popover.present();
  }

  async addTagToArticle(articleId: string) {
    this.searchablePopoverService.tags({
      event,
      callback: async (id: string) => {
        await this.tagsService.createKnowledgeBaseArticleLinks(id, [articleId]);
        await this.getKnowledgeBaseArticles();
      },
    });
  }

  getTagsForRow(row: any) {
    return row.tags?.map((tag) => tag.name).join(', ');
  }

  getUserGroupsForRow(row: any) {
    return row.userGroups?.map((userGroup) => userGroup.name).join(', ');
  }

  async addUserGroupToArticle(articleId: string) {
    const article = this.knowledgeBaseArticles.find((article) => article.id === articleId);

    this.searchablePopoverService.entities({
      event,
      type: ENTITY_TYPES.USER_GROUP,
      excludeIds: article.userGroupIds ?? [],
      callback: async (userGroupId: string, name: string) => {
        article.userGroupIds = [...(article.userGroupIds ?? []), userGroupId];
        article.userGroups = [...(article.userGroups ?? []), { id: userGroupId, name }];

        await this.knowledgeBaseArticlesService.updatePartial(articleId, {
          userGroupIds: article.userGroupIds,
        });
      },
    });
  }

  async showUserGroupOptions(articleId: string, userGroupId: string) {
    const LABEL_REMOVE_USER_GROUP = 'Remove user group';

    const options: ActionDropdownType[] = [
      {
        label: LABEL_REMOVE_USER_GROUP,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === LABEL_REMOVE_USER_GROUP) {
            const article = this.knowledgeBaseArticles.find((article) => article.id === articleId);

            article.userGroupIds = article.userGroupIds?.filter((id) => id !== userGroupId);
            article.userGroups = article.userGroups?.filter((userGroup) => userGroup.id !== userGroupId);

            await this.knowledgeBaseArticlesService.updatePartial(articleId, {
              userGroupIds: article.userGroupIds,
            });
          }
        },
      },
    });
    popover.present();
  }
}
